import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Container, Header, Segment, Form, Button, Message, Divider, Loader, Dimmer, Icon,
} from 'semantic-ui-react';
import { getProfile, updateProfile, changePassword, uploadAvatar } from './lib/tickets';
import { t } from './locales';

const AVATAR_PLACEHOLDER = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 96 96"><circle cx="48" cy="48" r="48" fill="%23e0d7f5"/><circle cx="48" cy="38" r="18" fill="%23b39ddb"/><ellipse cx="48" cy="82" rx="30" ry="22" fill="%23b39ddb"/></svg>';

function Profile() {
    const queryClient = useQueryClient();
    const fileInputRef = useRef(null);
    const [profileMsg, setProfileMsg] = useState(null);
    const [pwMsg, setPwMsg] = useState(null);
    const [avatarMsg, setAvatarMsg] = useState(null);
    const [pwData, setPwData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [avatarPreview, setAvatarPreview] = useState(null);

    const { data: profile, isLoading } = useQuery({
        queryKey: ['ticketing-profile'],
        queryFn: getProfile,
    });

    const [profileForm, setProfileForm] = useState(null);
    const formData = profileForm ?? {
        first_name: profile?.first_name ?? '',
        last_name: profile?.last_name ?? '',
        phone: profile?.phone ?? '',
        bio: profile?.bio ?? '',
        website: profile?.website ?? '',
        twitter_handle: profile?.twitter_handle ?? '',
        linkedin_url: profile?.linkedin_url ?? '',
        show_on_event_listings: profile?.show_on_event_listings ?? true,
    };

    const updateMutation = useMutation({
        mutationFn: (data) => updateProfile(null, data),
        onSuccess: () => {
            queryClient.invalidateQueries(['ticketing-profile']);
            setProfileMsg({ type: 'success', text: t('profile.profileUpdated') });
            setProfileForm(null);
            setTimeout(() => setProfileMsg(null), 3000);
        },
        onError: () => setProfileMsg({ type: 'error', text: t('profile.profileUpdateFailed') }),
    });

    const avatarMutation = useMutation({
        mutationFn: (file) => uploadAvatar(file),
        onSuccess: () => {
            queryClient.invalidateQueries(['ticketing-profile']);
            setAvatarMsg({ type: 'success', text: t('profile.avatarUpdated') });
            setTimeout(() => setAvatarMsg(null), 3000);
        },
        onError: () => {
            setAvatarMsg({ type: 'error', text: t('profile.avatarUploadFailed') });
        },
    });

    const pwMutation = useMutation({
        mutationFn: changePassword,
        onSuccess: async (response) => {
            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                setPwMsg({ type: 'error', text: body?.error || t('profile.currentPasswordIncorrect') });
                return;
            }
            setPwMsg({ type: 'success', text: t('profile.passwordChanged') });
            setPwData({ currentPassword: '', newPassword: '', confirmPassword: '' });
            setTimeout(() => setPwMsg(null), 3000);
        },
        onError: () => setPwMsg({ type: 'error', text: t('profile.passwordChangeFailed') }),
    });

    const handleAvatarChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const preview = URL.createObjectURL(file);
        setAvatarPreview(preview);
        avatarMutation.mutate(file);
    };

    const handleProfileSave = () => {
        setProfileMsg(null);
        updateMutation.mutate(formData);
    };

    const handlePasswordChange = () => {
        setPwMsg(null);
        if (pwData.newPassword !== pwData.confirmPassword) {
            setPwMsg({ type: 'error', text: t('profile.newPasswordMismatch') });
            return;
        }
        if (pwData.newPassword.length < 8) {
            setPwMsg({ type: 'error', text: t('profile.passwordTooShort') });
            return;
        }
        pwMutation.mutate({ currentPassword: pwData.currentPassword, newPassword: pwData.newPassword });
    };

    const set = (field) => (e) => {
        const val = e && e.target !== undefined ? e.target.value : e;
        setProfileForm(() => ({ ...formData, [field]: val }));
    };

    if (isLoading) {
        return (
            <Dimmer active inverted page>
                <Loader content={t('profile.loading')} />
            </Dimmer>
        );
    }

    const avatarSrc = avatarPreview || profile?.avatar || AVATAR_PLACEHOLDER;

    return (
        <Container className="contentContainer" style={{ maxWidth: 640 }}>
            <Header as="h1" className="sectionHeader" style={{ marginBottom: '1.5rem' }}>
                {t('profile.title')}
            </Header>

            {/* ── Avatar ─────────────────────────────────────────────────────── */}
            <Segment style={{ borderRadius: 12 }}>
                <Header as="h3" style={{ marginBottom: '1.25rem' }}>
                    <Icon name="camera" />
                    {t('profile.photoSectionTitle')}
                </Header>

                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
                    <div
                        style={{
                            width: 96, height: 96, borderRadius: '50%',
                            overflow: 'hidden', border: '3px solid var(--colour_menu_main)',
                            flexShrink: 0,
                        }}
                    >
                        <img
                            src={avatarSrc}
                            alt="Profile avatar"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    </div>
                    <div>
                        <input
                            type="file"
                            ref={fileInputRef}
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            style={{ display: 'none' }}
                            onChange={handleAvatarChange}
                        />
                        <Button
                            basic
                            size="small"
                            icon="upload"
                            content={avatarMutation.isLoading ? t('profile.uploading') : t('profile.uploadPhoto')}
                            onClick={() => fileInputRef.current?.click()}
                            loading={avatarMutation.isLoading}
                            disabled={avatarMutation.isLoading}
                        />
                        <p style={{ margin: '0.4rem 0 0', fontSize: '0.8rem', color: '#999' }}>
                            {t('profile.photoHint')}
                        </p>
                    </div>
                </div>

                {avatarMsg && (
                    <Message
                        positive={avatarMsg.type === 'success'}
                        negative={avatarMsg.type === 'error'}
                        size="small"
                        style={{ marginTop: '0.75rem' }}
                    >
                        {avatarMsg.text}
                    </Message>
                )}
            </Segment>

            <Divider hidden />

            {/* ── Personal Details ────────────────────────────────────────────── */}
            <Segment style={{ borderRadius: 12 }}>
                <Header as="h3">
                    <Icon name="user outline" />
                    {t('profile.personalDetailsTitle')}
                </Header>
                {profileMsg && (
                    <Message positive={profileMsg.type === 'success'} negative={profileMsg.type === 'error'}>
                        {profileMsg.text}
                    </Message>
                )}
                <Form onSubmit={handleProfileSave}>
                    <Form.Group widths="equal">
                        <Form.Input
                            label={t('profile.firstNameLabel')}
                            value={formData.first_name}
                            onChange={set('first_name')}
                        />
                        <Form.Input
                            label={t('profile.lastNameLabel')}
                            value={formData.last_name}
                            onChange={set('last_name')}
                        />
                    </Form.Group>
                    <Form.Input
                        label={t('profile.phoneLabel')}
                        value={formData.phone}
                        onChange={set('phone')}
                        placeholder={t('profile.phonePlaceholder')}
                    />
                    <Form.TextArea
                        label={t('profile.bioLabel')}
                        rows={3}
                        value={formData.bio}
                        onChange={set('bio')}
                        placeholder={t('profile.bioPlaceholder')}
                    />

                    <Divider horizontal style={{ color: '#aaa', fontSize: '0.8rem' }}>{t('profile.socialLinks')}</Divider>

                    <Form.Input
                        label={t('profile.websiteLabel')}
                        value={formData.website}
                        onChange={set('website')}
                        placeholder={t('profile.websitePlaceholder')}
                        icon="globe"
                        iconPosition="left"
                    />
                    <Form.Group widths="equal">
                        <Form.Input
                            label={t('profile.twitterLabel')}
                            value={formData.twitter_handle}
                            onChange={set('twitter_handle')}
                            placeholder={t('profile.twitterPlaceholder')}
                            icon="twitter"
                            iconPosition="left"
                        />
                        <Form.Input
                            label={t('profile.linkedinLabel')}
                            value={formData.linkedin_url}
                            onChange={set('linkedin_url')}
                            placeholder={t('profile.linkedinPlaceholder')}
                            icon="linkedin"
                            iconPosition="left"
                        />
                    </Form.Group>

                    <Divider horizontal style={{ color: '#aaa', fontSize: '0.8rem' }}>{t('profile.privacy')}</Divider>

                    <div
                        style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                            padding: '0.75rem', background: '#f9f0ff', borderRadius: 8,
                            border: '1px solid #e9d8fd', marginBottom: '1rem',
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <p style={{ margin: 0, fontWeight: 600, fontSize: '0.95rem', color: '#6b21a8' }}>
                                <Icon name="users" />
                                {t('profile.showOnAttendeeLists')}
                            </p>
                            <p style={{ margin: '0.2rem 0 0', fontSize: '0.85rem', color: '#555' }}>
                                {t('profile.attendeeListsDescription')}
                            </p>
                        </div>
                        <div style={{ marginLeft: '1rem', paddingTop: '2px' }}>
                            <input
                                type="checkbox"
                                checked={formData.show_on_event_listings}
                                onChange={e => setProfileForm(() => ({ ...formData, show_on_event_listings: e.target.checked }))}
                                style={{ width: 20, height: 20, accentColor: 'var(--colour_menu_main)', cursor: 'pointer' }}
                            />
                        </div>
                    </div>

                    <Button
                        type="submit"
                        loading={updateMutation.isLoading}
                        style={{ backgroundColor: 'var(--colour_menu_main)', color: '#fff', borderRadius: 8 }}
                    >
                        {t('profile.saveChanges')}
                    </Button>
                </Form>
            </Segment>

            <Divider hidden />

            {/* ── Change Password ─────────────────────────────────────────────── */}
            <Segment style={{ borderRadius: 12 }}>
                <Header as="h3">
                    <Icon name="lock" />
                    {t('profile.changePasswordTitle')}
                </Header>
                {pwMsg && (
                    <Message positive={pwMsg.type === 'success'} negative={pwMsg.type === 'error'}>
                        {pwMsg.text}
                    </Message>
                )}
                <Form>
                    <Form.Input
                        label={t('profile.currentPasswordLabel')}
                        type="password"
                        value={pwData.currentPassword}
                        onChange={e => setPwData(d => ({ ...d, currentPassword: e.target.value }))}
                    />
                    <Form.Input
                        label={t('profile.newPasswordLabel')}
                        type="password"
                        value={pwData.newPassword}
                        onChange={e => setPwData(d => ({ ...d, newPassword: e.target.value }))}
                    />
                    <Form.Input
                        label={t('profile.confirmNewPasswordLabel')}
                        type="password"
                        value={pwData.confirmPassword}
                        onChange={e => setPwData(d => ({ ...d, confirmPassword: e.target.value }))}
                    />
                    <Button
                        type="button"
                        onClick={handlePasswordChange}
                        loading={pwMutation.isLoading}
                        style={{ backgroundColor: 'var(--colour_menu_main)', color: '#fff', borderRadius: 8 }}
                    >
                        {t('profile.changePasswordButton')}
                    </Button>
                </Form>
            </Segment>
        </Container>
    );
}

export default Profile;
